import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { getSubProductConditions } from 'lib/api';
import {
  useCreateConfirmingOperation,
  useEditConfirmingOperation,
} from 'lib/api/confirming';
import {
  ConfirmingRequestById,
  DocumentFromConfirmingRequestById,
} from 'lib/api/confirming/types';
import {
  getAvailableArrearTables,
  getExecutivesAvailableForFactoring,
} from 'lib/api/factoring';
import { ArrearTable, Executive } from 'lib/api/factoring/types';
import CompanyBusinessSearcher from 'lib/common/CompanyBusinessSearcher';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import PageHeader from 'lib/designSystem/PageHeader';
import { getRealDaysToCount } from 'lib/functions/dates';
import { formatDate, showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { ConfirmingExportRequest } from '../../../common/SimulationExportDialog/types/ConfirmingExportRequest';
import ExportDialog from '../ExportDialog';
import ConfirmationDialog from './components/ConfirmationDialog';
import DatesSelector from './components/DatesSelector';
import DocumentSelector from './components/DocumentSelector';
import ProductSelector from './components/ProductSelector';
import Simulator from './components/Simulator';
import type { ConfirmingRequestForCreation } from './types';
import { RequestConfiguration } from './types';
import { createRequestForCreation } from './utils/CreateRequest';
import { getRestrictions } from './utils/GetRestrictions';

type NewOperationProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  parentSetUp: (id?: number) => void;
  editingRequest: ConfirmingRequestById | null;
};

function getTodayUTC(): Date {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
  );
}

const NewOperation = (props: NewOperationProps) => {
  const { open, setOpen, parentSetUp, editingRequest } = props;
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [business, setBusiness] = useState<any>({});
  const [searched, setSearched] = useState<boolean>(false);
  const [step, setStep] = useState<number>(-1);

  // Documents
  const [documents, setDocuments] = useState<any[]>([]);
  const [cachedDocuments, setCachedDocuments] = useState<any[]>([]);
  const [selected, setSelected] = useState<any[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<any[]>([]);

  const [productType, setProductType] = useState<
    'traditional' | 'earlyPayment'
  >('traditional');

  const [operationDate, setOperationDate] = useState<string>(
    getTodayUTC().toISOString().split('T')[0],
  );
  const [operationDateDate, setOperationDateDate] =
    useState<Date>(getTodayUTC());
  const [canForcePaymentDate, setCanForcePaymentDate] =
    useState<boolean>(false);
  const [minimumDays, setMinimumDays] = useState<number>(0);
  const [maximumDays, setMaximumDays] = useState<number>(0);

  const [requestForCreation, setRequestForCreation] =
    useState<ConfirmingRequestForCreation | null>(null);
  const [availableArrearTables, setAvailableArrearTables] = useState<
    ArrearTable[]
  >([]);
  const [requestConfiguration, setRequestConfiguration] =
    useState<RequestConfiguration>({
      expensesHasIVA: false,
      fixedArrearRate: false,
      arrearRate: 0,
      exchangeDays: 0,
    });
  const [subProductConditions, setSubProductConditions] = useState<{
    fundRate: number;
  }>({
    fundRate: 0,
  });
  const [executivesPortfolio, setExecutivesPortfolio] = useState<Executive[]>(
    [],
  );
  const [executivesAll, setExecutivesAll] = useState<Executive[]>([]);

  const [openConfirmationDialog, setOpenConfirmationDialog] =
    useState<boolean>(false);
  const [openExportDialog, setOpenExportDialog] = useState(false);

  const {
    mutate: createConfirmingOperation,
    isPending: isCreatingConfirmingOperation,
  } = useCreateConfirmingOperation(dispatch);
  const {
    mutate: editConfirmingOperation,
    isPending: isEditingConfirmingOperation,
  } = useEditConfirmingOperation(dispatch);

  const fetchExecutives = async () => {
    if (business?.identifier) {
      try {
        const response = await getExecutivesAvailableForFactoring(
          dispatch,
          business.identifier,
        );
        if (response) {
          setExecutivesPortfolio(response.usersWithBusinessInPortfolio);
          setExecutivesAll(response.allUsers);
        }
      } catch (error) {
        console.error('Error fetching executives:', error);
      }
    }
  };

  const setUpAvailableArrearTables = async () => {
    const availableArrearTables = await getAvailableArrearTables(dispatch);
    setAvailableArrearTables(availableArrearTables || []);
  };

  const setUpSubProductConditions = async () => {
    try {
      const subProductConditions = await getSubProductConditions(dispatch, {
        businessId: business.identifier,
        subProductId: 'electronic-invoice-confirming',
      });
      if (!subProductConditions) return;
      setSubProductConditions(subProductConditions);
    } catch (error) {
      console.error('Error fetching subProduct conditions:', error);
    }
  };

  const handleExportSimulation = async () => {
    if (!requestForCreation) return;
    setOpenExportDialog(true);
  };

  const goBack = () => {
    if (step === 1) {
      setBusiness({});
      setSearched(false);
    }
    if (step === 3) {
      if (editingRequest) {
        return setStep(step - 2);
      }
    }
    setStep(step - 1);
  };

  const goNext = () => {
    if (step === 1) {
      const finalDocuments = [];
      for (const id of selected) {
        const document = cachedDocuments.find(
          (document: any) => document.id === id,
        );
        const selectedDocument = selectedDocuments.find(
          (document: any) => document.id === id,
        );
        if (document) {
          finalDocuments.push(selectedDocument || document);
        }
      }
      setSelectedDocuments(finalDocuments);
      if (editingRequest) {
        return setStep(step + 2);
      }
      return setStep(step + 1);
    }
    if (step === 3) {
      if (!checkSelectedDocuments()) {
        return;
      }
      setRequestForCreation(
        createRequestForCreation(
          selectedDocuments,
          editingRequest,
          requestConfiguration,
          productType,
          operationDateDate,
          subProductConditions,
        ),
      );
      return setStep(step + 1);
    }

    setStep(step + 1);
  };

  useEffect(() => {
    if (step === 0 && searched) {
      goNext();
    }
  }, [step, searched]);

  useEffect(() => {
    if (business?.identifier) {
      void setUpSubProductConditions();
    }
  }, [business?.identifier]);

  const hiddenGoBack = () => {
    switch (step) {
      case 1:
      case 2:
        if (editingRequest) {
          return true;
        }
        return false;
      case 3:
      case 4:
        return false;
      case 0:
      default:
        return true;
    }
  };

  const disabledGoBack = () => {
    switch (step) {
      case 0:
        return true;
      case 1:
        return false;
      case 2:
        return false;
      case 3:
        return false;
      case 4:
        return false;
      default:
        return true;
    }
  };

  const hiddenGoNext = () => {
    switch (step) {
      case 0:
        return true;
      case 1:
        return false;
      case 2:
        return false;
      case 3:
        return false;
      default:
        return true;
    }
  };

  const checkSelectedDocuments = () => {
    const duplicatedFolios: string[] = [];
    selectedDocuments.forEach((document: any) => {
      if (!document.oldSubDocuments) return;
      if (document.subDocuments?.length) {
        const subFolios = document.oldSubDocuments.map(
          (subDocument: any) => subDocument.folio,
        );
        document.subDocuments.forEach((subDocument: any) => {
          const key = `${document.folio}-${subDocument.folio}`;
          if (subFolios.includes(key)) {
            duplicatedFolios.push(key);
          }
        });
      }
    });
    if (duplicatedFolios.length) {
      return false;
    }

    const paymentDate = selectedDocuments.every((document: any) => {
      if (document.subDocuments?.length) {
        return document.subDocuments.every((subDocument: any) => {
          return subDocument.paymentDate;
        });
      } else {
        return document.paymentDate;
      }
    });
    if (!paymentDate) {
      return false;
    }
    const amount = selectedDocuments.every((document: any) => {
      if (document.subDocuments?.length) {
        return document.subDocuments.every((subDocument: any) => {
          return subDocument.amount;
        });
      } else {
        return document.amount;
      }
    });
    if (!amount) {
      return false;
    }
    return true;
  };

  const disabledGoNext = () => {
    switch (step) {
      case 0:
        return true;
      case 1:
        if (selected.length === 0) {
          return true;
        }
        return false;
      case 2:
        return false;
      case 3:
        if (operationDate === 'none' || !operationDate) {
          return true;
        }
        if (selectedDocuments.length === 0) {
          return true;
        }
        if (!checkSelectedDocuments()) {
          return true;
        }
        return false;
      default:
        return true;
    }
  };

  const confirmNewOperation = async () => {
    if (!requestForCreation) {
      return;
    }
    createConfirmingOperation(
      { request: requestForCreation },
      {
        onSuccess: (response) => {
          setOpenConfirmationDialog(false);
          showAlert(dispatch, 'success', 'Operación creada correctamente');
          parentSetUp(response?.id || undefined);
          setOpen(false);
        },
      },
    );
  };

  const confirmEditOperation = async () => {
    if (!requestForCreation) {
      return;
    }
    editConfirmingOperation(
      { request: requestForCreation },
      {
        onSuccess: (response) => {
          setOpenConfirmationDialog(false);
          showAlert(dispatch, 'success', 'Operación editada correctamente');
          parentSetUp(response?.id || undefined);
          setOpen(false);
        },
      },
    );
  };

  const confirmOperation = async () => {
    if (editingRequest) {
      return confirmEditOperation();
    }
    return confirmNewOperation();
  };

  const setUp = async () => {
    if (editingRequest) {
      setUpEditOperation();
    }

    await getRestrictions(
      user,
      dispatch,
      setRequestConfiguration,
      setRequestForCreation,
      setCanForcePaymentDate,
      setMinimumDays,
      setMaximumDays,
      requestConfiguration,
      requestForCreation,
    );
    await setUpAvailableArrearTables();

    if (editingRequest) {
      // Fetch executives after business is set
      await fetchExecutives();
      setStep(1);
    } else {
      await fetchExecutives();
      setStep(0);
    }
  };

  const setUpEditOperation = () => {
    setBusiness({
      identifier: editingRequest?.stakeholderIdentifier,
      businessName: editingRequest?.stakeholderName,
    });
    const requestDocuments = editingRequest?.documents?.map(
      (document: DocumentFromConfirmingRequestById) => {
        const date =
          typeof document.paymentDate === 'string'
            ? new Date(document.paymentDate)
            : document.paymentDate;
        return {
          ...document,
          dueDate: date.toISOString(),
          paymentDate: date,
          tempDueDate: formatDate(date),
          daysToCount: getRealDaysToCount(
            date,
            new Date(editingRequest.operationDate),
          ),
          code: document.subType,
          userOwnerId: editingRequest.userOwner?.id || undefined,
        };
      },
    );
    setSelectedDocuments(requestDocuments ?? []);
    setSelected(requestDocuments?.map((document) => document.id) ?? []);

    const executiveId =
      editingRequest?.userOwner?.id || editingRequest?.userOwnerId;

    setRequestForCreation((prev) =>
      prev
        ? { ...prev, userOwnerId: executiveId }
        : ({ userOwnerId: executiveId } as ConfirmingRequestForCreation),
    );
  };

  useEffect(() => {
    void setUp();
  }, []);

  useEffect(() => {
    if (business?.identifier) {
      void fetchExecutives();
    }
  }, [business]);

  useEffect(() => {
    if (
      editingRequest &&
      requestForCreation &&
      !requestForCreation.userOwnerId
    ) {
      const executiveId =
        editingRequest.userOwner?.id || editingRequest.userOwnerId;
      if (executiveId) {
        setRequestForCreation((prev) =>
          prev
            ? { ...prev, userOwnerId: executiveId }
            : ({ userOwnerId: executiveId } as ConfirmingRequestForCreation),
        );
      }
    }
  }, [requestForCreation, editingRequest]);

  if (step === -1) {
    return <CenteredCircularProgress />;
  }

  const isLoading =
    isCreatingConfirmingOperation || isEditingConfirmingOperation;

  return (
    <Dialog open={open} maxWidth="xl" fullWidth>
      <DialogTitle>
        <PageHeader.TitleOnly
          title={editingRequest ? 'Editar operación' : 'Crear operación'}
          subtitle="Confirming"
          variant="dialogHeader"
          iconType={editingRequest ? 'edit' : 'add'}
        />
      </DialogTitle>
      <DialogContent>
        <div>
          {isLoading && <CenteredCircularProgress type="dialog" />}
          {step === 0 && (
            <CompanyBusinessSearcher
              setBusiness={setBusiness}
              setSearched={setSearched}
            />
          )}
          {step === 1 && (
            <DocumentSelector
              business={business}
              selected={selected}
              setSelected={setSelected}
              documents={documents}
              setDocuments={setDocuments}
              cachedDocuments={cachedDocuments}
              setCachedDocuments={setCachedDocuments}
              selectedDocuments={selectedDocuments}
              setSelectedDocuments={setSelectedDocuments}
            />
          )}
          {step === 2 && (
            <ProductSelector
              business={business}
              productType={productType}
              setProductType={setProductType}
            />
          )}
          {step === 3 && (
            <DatesSelector
              business={business}
              selectedDocuments={selectedDocuments}
              setSelectedDocuments={setSelectedDocuments}
              productType={productType}
              operationDate={operationDate}
              setOperationDate={setOperationDate}
              operationDateDate={operationDateDate}
              setOperationDateDate={setOperationDateDate}
              canForcePaymentDate={canForcePaymentDate}
              minimumDays={minimumDays}
              maximumDays={maximumDays}
            />
          )}
          {step === 4 && (
            <Simulator
              requestForCreation={requestForCreation}
              setRequestForCreation={setRequestForCreation}
              availableArrearTables={availableArrearTables}
              requestConfiguration={requestConfiguration}
              executivesPortfolio={executivesPortfolio}
              executivesAll={executivesAll}
              onExportSimulation={handleExportSimulation}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          Salir
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={() => goBack()}
          disabled={disabledGoBack()}
          hidden={hiddenGoBack()}
        >
          Volver
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={() => goNext()}
          disabled={disabledGoNext()}
          hidden={hiddenGoNext()}
        >
          Continuar
        </CustomButton>
        {editingRequest ? (
          <CustomButton
            color="success"
            onClick={() => setOpenConfirmationDialog(true)}
            hidden={step != 4}
          >
            Editar
          </CustomButton>
        ) : (
          <CustomButton
            color="success"
            onClick={() => setOpenConfirmationDialog(true)}
            hidden={step != 4}
          >
            Crear
          </CustomButton>
        )}
      </DialogActions>
      <ConfirmationDialog
        open={openConfirmationDialog}
        editingRequest={!!editingRequest}
        onClose={() => setOpenConfirmationDialog(false)}
        onConfirm={() => void confirmOperation()}
        loading={isLoading}
      />
      <ExportDialog
        open={openExportDialog}
        setOpen={setOpenExportDialog}
        selectedRequest={
          requestForCreation as unknown as ConfirmingExportRequest
        }
      />
    </Dialog>
  );
};

export default NewOperation;
